import { QueryClient, skipToken, useMutation, useQueryClient } from "@tanstack/react-query";

import { OurUseQueryOptions } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { ldoApiV1 } from "../api";
import {
  ApiBulkExport,
  ApiBulkExportCanStart,
  BulkExportType,
  EXPORT_STATUSES_EXCEPT_ARCHIVED,
  PaginatedApiBulkExportList,
  bulkExportCanStartSchema,
  bulkExportSchema,
  bulkExportsListSchema,
} from "../schemas/bulk-exports";

export const getBulkExports = (
  exportType: BulkExportType | "all",
  params: Record<string, string | number> = {}
) =>
  ldoApiV1.getQueryOptions<PaginatedApiBulkExportList>({
    queryKey: ["bulk-exports", "list", exportType, params],
    queryFn: async (): Promise<PaginatedApiBulkExportList> => {
      const theParams = { ...params };
      if (exportType !== "all") {
        theParams.type = exportType;
      }
      let url = combineUrlAndParams("/bulk-exports", theParams);
      if (params.mode === "private" && params.status === undefined) {
        // In private mode, we search for all statuses but "archived"
        // In public mode we don't because that already only shows shared exports,
        // and it gives an error if we add a status.
        if (url.indexOf("?") === -1) {
          url = url + "?";
        } else {
          url = url + "&";
        }
        url += EXPORT_STATUSES_EXCEPT_ARCHIVED.map((status) => `status=${status}`).join("&");
      }

      return bulkExportsListSchema.parse(await ldoApiV1.fetchJson(url));
    },
  });

const refreshBulkExportsList = async (queryClient: QueryClient) => {
  const listQueryKey = getBulkExports("all").queryKey.slice(0, 4);
  return queryClient.invalidateQueries({
    queryKey: listQueryKey,
    refetchInactive: true, // Otherwise lists on other pages aren't refreshed!
    refetchActive: true,
  });
};

export function getBulkExport(
  id: number
): OurUseQueryOptions<ApiBulkExport, () => Promise<ApiBulkExport>>;
export function getBulkExport(id: null): OurUseQueryOptions<ApiBulkExport, typeof skipToken>;
export function getBulkExport(id: number | null): OurUseQueryOptions<ApiBulkExport>;
export function getBulkExport(id: number | null): OurUseQueryOptions<ApiBulkExport> {
  return ldoApiV1.getQueryOptions({
    queryKey: ["bulk-exports", "detail", id],
    queryFn:
      id === null
        ? skipToken
        : async (): Promise<ApiBulkExport> =>
            bulkExportSchema.parse(await ldoApiV1.fetchJson(`/bulk-exports/${id}`)),
  });
}

export const getBulkExportCanStart = (
  id: number | null
): OurUseQueryOptions<ApiBulkExportCanStart> =>
  ldoApiV1.getQueryOptions<ApiBulkExportCanStart>({
    queryKey: ["bulk-exports", "detail", id, "can-start"],
    queryFn: async (): Promise<ApiBulkExportCanStart> =>
      bulkExportCanStartSchema.parse(await ldoApiV1.fetchJson(`/bulk-exports/${id}/can-start`)),
  });

export function useUpdateBulkExport(bulkExportId: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<ApiBulkExport>) => {
      if (bulkExportId === null) return null;
      const url = `/bulk-exports/${bulkExportId}`;

      const response = await ldoApiV1.fetchJson(url, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
      const newExport = bulkExportSchema.parse(response);
      refreshBulkExportsList(queryClient);
      queryClient.setQueryData(getBulkExport(newExport.id).queryKey, newExport);
    },
  });
}

export function useDeleteBulkExport(bulkExport: ApiBulkExport | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (bulkExport === null) return null;
      const url = `/bulk-exports/${bulkExport.id}`;
      const response = await ldoApiV1.fetch(url, { method: "DELETE" });

      refreshBulkExportsList(queryClient);
      return response;
    },
  });
}

export function useCreateBulkExport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      exportType,
      name,
      description,
      config,
      extra,
    }: {
      exportType: BulkExportType;
      name: string;
      description: string;
      config: ApiBulkExport["config"];
      extra: Partial<ApiBulkExport>;
    }) => {
      let response = await ldoApiV1.fetchJson("/bulk-exports", {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({
          name,
          description,
          type: exportType,
          config,
        }),
      });

      if (Object.keys(extra).length > 0) {
        // Other things can only be patched after creating
        const bulkExport = bulkExportSchema.parse(response);
        const url = `/bulk-exports/${bulkExport.id}`;
        response = await ldoApiV1.fetchJson(url, {
          method: "PATCH",
          body: JSON.stringify(extra),
        });
      }

      refreshBulkExportsList(queryClient);
      return bulkExportSchema.parse(response);
    },
  });
}

export function useRemoveScenarioFromBulkExport(bulkExportId: number, scenarioId: number) {
  const updateBulkExport = useUpdateBulkExport(bulkExportId);

  return useMutation({
    mutationFn: async () => {
      const detailQueryFn = getBulkExport(bulkExportId).queryFn;

      // First GET the bulkExport for the given ID, take the scenarioIds and omit the given scenarioId
      const response = await detailQueryFn();
      const ids = response.scenario_ids;
      const scenario_ids = ids.filter((id) => id !== scenarioId);

      // Then use the new array in the PATCH
      return updateBulkExport.mutateAsync({ scenario_ids });
    },
  });
}
